import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { CircleAlertBold } from "@/components/icons";
import * as Sentry from "@sentry/react";
import { isChunkLoadError } from "@/common/utils/lazyRoute";

interface ErrorInfo {
  title: string;
  message: string;
}

function getErrorInfo(error: unknown): ErrorInfo {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        title: "페이지를 찾을 수 없습니다",
        message: "요청하신 페이지가 존재하지 않거나 이동되었습니다.",
      };
    }
    if (error.status === 401) {
      return {
        title: "인증이 필요합니다",
        message: "로그인 후 다시 시도해주세요.",
      };
    }
    if (error.status === 403) {
      return {
        title: "접근 권한이 없습니다",
        message: "해당 페이지에 접근할 수 있는 권한이 없습니다.",
      };
    }
    return {
      title: `오류가 발생했습니다 (${error.status})`,
      message: error.statusText || "알 수 없는 오류가 발생했습니다.",
    };
  }

  // 배포로 청크 해시가 갈린 경우. lazyRoute 의 자동 새로고침까지 실패한 뒤라
  // 사용자에게 원문 대신 다음 행동을 안내한다
  if (isChunkLoadError(error)) {
    return {
      title: "새 버전이 배포되었어요",
      message: "다시 시도하면 최신 버전으로 이어서 사용할 수 있어요.",
    };
  }

  if (error instanceof Error) {
    return {
      title: "오류가 발생했습니다",
      message: error.message,
    };
  }

  return {
    title: "오류가 발생했습니다",
    message: "알 수 없는 오류가 발생했습니다.",
  };
}

export function PageErrorBoundary() {
  const error = useRouteError();
  const { reset } = useQueryErrorResetBoundary();
  const errorInfo = getErrorInfo(error);

  // 라우트 에러 발생 시 Sentry로 에러 정보를 전송하여 대시보드에서 모니터링 가능
  Sentry.captureException(error);

  const handleRetry = () => {
    reset();
    window.location.reload();
  };

  const handleGoHome = () => {
    reset();
    window.location.href = "/";
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 p-8">
      <CircleAlertBold color="status-urgent" className="size-16" />
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="typo-heading-3-semibold text-labels-primary">
          {errorInfo.title}
        </h1>
        <p className="typo-body-2-medium text-labels-secondary">
          {errorInfo.message}
        </p>
      </div>
      <div className="flex gap-3">
        <Button variant="outline" theme="assistive" onClick={handleGoHome}>
          홈으로 이동
        </Button>
        <Button theme="normal" onClick={handleRetry}>
          다시 시도
        </Button>
      </div>
    </div>
  );
}
